/* eslint-disable no-console */
import React, {useEffect, useMemo, useState} from 'react';
import { TextField, Grid, Tooltip, IconButton, Box, Typography,InputAdornment } from '@mui/material';
import addCircle from '../../assets/SVG/add-black.svg';
import styles from './DiagnosisHome.module.css';
import EditableTable from '../customTableComponent';
import { ColumnConfig, GroupConfig, TableRowData } from '../../types';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import AddDiagnosis from './AddDiagnosis';
import { useLoader } from '../loaderProvider/loader';
import {  deleteDig, getPatientDiagnosis } from './diagnosisHelper';
import { usePatientContext } from '../SubPatinetProvider';
import deleteBtn from '../../assets/SVG/trash.svg';
import { ConfirmationModal } from '../confirmationModal';
import { useAlert } from '../alertProvider';
import { useTranslation } from 'react-i18next';
import RemarkPopover from '../Billing/common/RemarkPopover';
import { ReactComponent as SearchIcon } from '../../assets/SVG/Search-Icon.svg';
import { formatDate } from '../../utils/dateUtils';


const DiagnosisHome: React.FC<{ onAddClick: () => void; permissions?: { canAdd?: boolean; canEdit?: boolean }; onDataCountChange?: (count: number) => void }> = ({ onAddClick, permissions, onDataCountChange }) => {

  const { t : tGeneral} = useTranslation('general');
  const { t : tInvestigation } = useTranslation('investigation_config');
  const columns: ColumnConfig[] = [
    { field: 'date', headerName: tGeneral('Date'), type: 'text', editable: false },
    { field: 'code', headerName: tGeneral('TestCode'), type: 'text', editable: false },
    { field: 'name', headerName: tGeneral('TestName'), type: 'text', editable: false },
    { field: 'type', headerName: tGeneral('Type'), type: 'text', editable: false },
    { field: 'doctor', headerName: tGeneral('DoctorName'), type: 'text', editable: false },
    { field: 'clinic', headerName: tGeneral('Clinic'), type: 'text', editable: false },
    { field: 'notes', headerName: tGeneral('Notes'), type: 'text', editable: false },
    { field: 'action', headerName: '', type: 'text', editable: false },

  ];


  const groupConfig: GroupConfig[] = [
    { fields: ['date'], type: 'column' },
    { fields: ['code'], type: 'column' },
    { fields: ['name'], type: 'column' },
    { fields: ['type'], type: 'column' },
    { fields: ['doctor'], type: 'column' },
    { fields: ['clinic'], type: 'column' },
    { fields: ['notes'], type: 'column' },
    { fields: ['action'], type: 'column' },
  ];

  const [data, setData] = useState<TableRowData[]>([]);
  const [showAddDiagnosis, setShowAddDiagnosis] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { patientId } = usePatientContext();
  const { showLoader, hideLoader } = useLoader();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedData, setSelectedData] = useState<any | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false); 
  const [selectedDigId, setselecteddigId] = useState<number | null>(null);
  const [IsdiagDeleted, setIsdiagDeleted] = useState(false);
  const { addAlert } = useAlert();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage] = useState<number>(10);
  const [paginationMeta, setPaginationMeta] = useState<{limit: number; offset: number; total_count: number}>({limit: 10, offset: 0, total_count: 0});
  
  // Notes popover state
  const [notesAnchorEl, setNotesAnchorEl] = useState<null | HTMLElement>(null);
  const [notesText, setNotesText] = useState<string>('');

  const handleOpenNotes = (event: React.MouseEvent<HTMLElement>, text: string) => {
    setNotesAnchorEl(event.currentTarget);
    setNotesText(text);
  };

  const handleCloseNotes = () => {
    setNotesAnchorEl(null);
    setNotesText('');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAction = (id: number) => {
  };

  const handleDelete = (id: number) => {
    setselecteddigId(id);
    setOpenConfirmDialog(true);
  };

  useEffect(() => {
    showLoader();
    const offset = (currentPage - 1) * rowsPerPage;
    getPatientDiagnosis(patientId, rowsPerPage, offset)
      .then((res) => {
        setPaginationMeta({
          limit: res.meta.limit,
          offset: res.meta.offset,
          total_count: res.meta.total_count
        }); 
        const tableData: TableRowData[] = res.objects.map((item) => {
          const createdOnDate = item.created_on ? new Date(item.created_on) : null;
          const isDisabled = createdOnDate
            ? (new Date().getTime() - createdOnDate.getTime()) / (1000 * 60 * 60) > 24
            : false;
        
          console.log(isDisabled);
            
          return {
            id: item.id,
            date: formatDate(item.created_on) || '-',
            code: item.diagnosis_code,
            name: item.diagnosis_name_label || item.diagnosis_name,
            type: item.diagnosis_type_label || item.diagnosis_type,
            doctor: `${item.doctor_name_label || item.doctor_name}`,
            clinic: item.clinic || '-',
            notes: item.notes
              ? item.notes.length > 30 ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Tooltip title={tGeneral('Notes')} arrow>
                    <IconButton
                      size="small"
                      sx={{ p: 0, color: '#5A8AEA', flexShrink: 0 }}
                      aria-label={tGeneral('ViewNotes')}
                      onClick={(e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        handleOpenNotes(e, item.notes!);
                      }}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Typography>{item.notes.slice(0, 30)}...</Typography>
                </Box>
              ) : (
                <span>{item.notes}</span>
              )
              : '-',
            action: (
              permissions?.canEdit ? (

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isDisabled ? 'not-allowed' : 'pointer' }}>
                  <Tooltip title={isDisabled ? tGeneral('CannotDeletePast24Hours') : tGeneral('Delete')}>
                    <span>
                      <IconButton
                        onClick={() => !isDisabled && handleDelete(item.id)}
                        disabled={isDisabled}
                        style={{ color: isDisabled ? '#B0B0B0' : '#FF0000' }}
                        size="small"
                      >
                        <img src={deleteBtn} alt={tGeneral('Delete')} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              ) : null

            ),
          };
        });
        
        setData(tableData);
        if (onDataCountChange) {
          onDataCountChange(res.meta.total_count);
        }
        
      })
      .catch((error) => {
        console.error('Error fetching patient diagnosis:', error);
      })
      .finally(() => {
        hideLoader();
        setIsdiagDeleted(false); 
      });
  }, [patientId, showAddDiagnosis, showLoader, hideLoader, IsdiagDeleted, onDataCountChange, currentPage, rowsPerPage, permissions, tGeneral]);

  const handleSubmit =()=>{
    showLoader();

    console.log('Deleting row with ID:', selectedDigId);
    if (selectedDigId) {
      deleteDig(selectedDigId).then(()=>{
        addAlert(tInvestigation('DiagnosisDeletedSuccessfully'), 'success');
        setIsdiagDeleted(true);
      })
        .catch((error) => {
          console.error('Error deleting selected Drug', error);
          addAlert(tGeneral('ErrorDeletingSelectedDrug'), 'error');
          
        })
        .finally(() => {
          hideLoader();
        });
    }
  };

  const filteredData = useMemo(
    () => data.filter(
      (row) =>
        row.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.name.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [searchTerm, data]
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOnChange = (id: number, field: string, value: string) => {

  };
  


  const handleAddClick = () => {
    setSelectedData(null);
    setShowAddDiagnosis(true);
    onAddClick();
  };
  // const handleAddClickEdit = () => {
  //   setShowAddDiagnosis(true);
  //   onAddClick();
  // };
  const handleBackClick = () => {
    setShowAddDiagnosis(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };


  return (
    <div className={`${styles.home} mt-4`} style={{ display: 'flex', flexDirection: 'column' }} data-testid="diagnosis-home-container">
      {showAddDiagnosis ? (
        <AddDiagnosis onBackClick={handleBackClick} data={selectedData} />
      ) : (
        <>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={6} lg={4} xl={4}>
              <TextField
                id="outlined-basic"
                placeholder={tInvestigation('text_SearchByCodeOrName')}
                value={searchTerm}
                onChange={handleSearchChange}
                variant="outlined"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }
                }}
                fullWidth 
                data-testid="diagnosis-home-search-input"/>
            </Grid>
            <Grid item xs />
            { permissions?.canAdd && (
              <Grid item>
                <button onClick={handleAddClick} data-testid="diagnosis-home-add-button" style={  {padding: '3px 20px', border: 'none', borderRadius: '7px', fontSize:'18px', fontWeight:'700', backgroundColor:'#F3F3F3F3', color:'#505050' }}>
                  <img style={{padding:'5px', width:'37px', height:'37px'}} src={addCircle} alt={tGeneral('Add')}/>   {tGeneral('Add')}
                </button>
              </Grid>
            )}
          </Grid>

          <div className="mt-4" style={{ flexGrow: 1 }}>
            <EditableTable
              columns={columns}
              data={searchTerm ? filteredData : data}
              actionType="none"
              onChange={handleOnChange}
              onRowClick={handleAction}
              groupConfig={groupConfig}
              paginationMeta={searchTerm ? undefined : paginationMeta}
              onPageChange={handlePageChange}
              currentPageoffset={currentPage}
              isAppointmentFiltered={!!searchTerm}
              data-testid="diagnosis-home-table"
            />
          </div>
        </>
      )}

      <ConfirmationModal
        open={openConfirmDialog}
        handleClose={()=>{setOpenConfirmDialog(false);}}
        onConfirm={() => {
          handleSubmit();
        }}
        type="save"
        title={tGeneral('DeleteDiagnosis')}
        contentMessage={tGeneral('AreYouSureDeletePrescribedDiagnosis')}
        data-testid="diagnosis-home-delete-modal"
      />
      <RemarkPopover
        anchorEl={notesAnchorEl}
        content={notesText}
        open={Boolean(notesAnchorEl)}
        onClose={handleCloseNotes}
        title={tGeneral('Notes')}
        data-testid="diagnosis-home-notes-popover"
      />
    </div>
  );
};

export default DiagnosisHome;
